import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";

import { createProject, updateProject, type Project, type ProjectPayload } from "@/api/development_suite";
import { listMunicipalityCollaborators } from "@/api/municipality_collaborators";
import { blankToNull, developmentSuiteKeys } from "@/components/development_suite/developmentSuiteQueries";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const NO_RESPONSIBLE_STAFF = "none";

type FormValues = {
  name: string;
  description: string;
  siteAddress: string;
  parcelIdentifier: string;
  responsibleUserId: string;
};

function initialValues(project: Project | null): FormValues {
  return {
    name: project?.name ?? "",
    description: project?.description ?? "",
    siteAddress: project?.site_address ?? "",
    parcelIdentifier: project?.parcel_identifier ?? "",
    responsibleUserId: project?.responsible_user ? String(project.responsible_user.id) : NO_RESPONSIBLE_STAFF,
  };
}

type ProjectFormDialogProps = {
  municipalityId: number;
  /** The project to edit, or null to create one. */
  project: Project | null;
  onClose: () => void;
  onSaved: (project: Project) => void;
};

/** Mount only while open, so each open starts from the saved values. */
export function ProjectFormDialog({ municipalityId, project, onClose, onSaved }: ProjectFormDialogProps) {
  const [values, setValues] = useState<FormValues>(() => initialValues(project));
  // The dialog unmounts on close, so Radix cannot restore focus; return it to
  // the control that opened the dialog so keyboard users keep their place.
  const openerRef = useRef(document.activeElement as HTMLElement | null);
  useEffect(() => {
    const opener = openerRef.current;
    return () => {
      setTimeout(() => opener?.isConnected && opener.focus(), 0);
    };
  }, []);
  const [nameMissing, setNameMissing] = useState(false);

  const staffQuery = useQuery({
    queryKey: ["municipality_collaborators", municipalityId],
    queryFn: () => listMunicipalityCollaborators(municipalityId),
    staleTime: 60_000,
  });

  // A failed save leaves the dialog open with what was typed.
  const mutation = useMutation({
    mutationKey: developmentSuiteKeys.all(municipalityId),
    mutationFn: (payload: ProjectPayload) =>
      project ? updateProject(municipalityId, project.id, payload) : createProject(municipalityId, payload),
    onSuccess: onSaved,
  });

  // Clears only a settled error; resetting a pending save would re-enable Submit mid-write.
  const clearSaveError = () => {
    if (mutation.isError) mutation.reset();
  };
  // An edit or a local validation failure makes the last server error stale.
  const setField = (field: keyof FormValues) => (value: string) => {
    clearSaveError();
    if (field === "name") setNameMissing(false);
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!values.name.trim()) {
      clearSaveError();
      setNameMissing(true);
      document.getElementById("project-name")?.focus();
      return;
    }
    setNameMissing(false);
    mutation.mutate({
      name: values.name.trim(),
      description: blankToNull(values.description),
      site_address: blankToNull(values.siteAddress),
      parcel_identifier: blankToNull(values.parcelIdentifier),
      responsible_user_id:
        values.responsibleUserId === NO_RESPONSIBLE_STAFF ? null : Number(values.responsibleUserId),
    });
  };

  // Keep the saved assignee selectable even if they no longer appear in the
  // staff list, so opening the form never silently changes the assignment.
  const staffOptions = [...(staffQuery.data ?? [])];
  if (project?.responsible_user && !staffOptions.some((user) => user.id === project.responsible_user?.id)) {
    staffOptions.unshift(project.responsible_user);
  }

  const saving = mutation.isPending;

  return (
    <Dialog open onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>{project ? "Edit project" : "New project"}</DialogTitle>
            <DialogDescription>
              {project
                ? "Changes apply to this project only. Its permits and planning records are not changed."
                : "A project can start empty. Add permits and planning records once it is saved."}
            </DialogDescription>
          </DialogHeader>

          {mutation.error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>The project was not saved</AlertTitle>
              <AlertDescription>
                {mutation.error.message} Your changes are still here, so you can correct them and try again.
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Fields lock while saving so nothing typed mid-save is silently dropped. */}
          <fieldset disabled={saving} className="min-w-0 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Name</Label>
              <Input
                id="project-name"
                value={values.name}
                onChange={(event) => setField("name")(event.target.value)}
                placeholder="e.g. Riverside Commons"
                maxLength={255}
                aria-invalid={nameMissing}
                aria-describedby={nameMissing ? "project-name-error" : undefined}
              />
              {nameMissing ? (
                <p id="project-name-error" className="text-sm text-destructive">
                  Enter a project name.
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={values.description}
                onChange={(event) => setField("description")(event.target.value)}
                rows={3}
                maxLength={10_000}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="project-site-address">Site address</Label>
                <Input
                  id="project-site-address"
                  value={values.siteAddress}
                  onChange={(event) => setField("siteAddress")(event.target.value)}
                  maxLength={255}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-parcel">Parcel ID</Label>
                <Input
                  id="project-parcel"
                  value={values.parcelIdentifier}
                  onChange={(event) => setField("parcelIdentifier")(event.target.value)}
                  maxLength={255}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-responsible-staff">Responsible staff</Label>
              <Select
                value={values.responsibleUserId}
                onValueChange={setField("responsibleUserId")}
                // Radix reads only its own prop, not the surrounding disabled fieldset.
                disabled={saving}
              >
                <SelectTrigger id="project-responsible-staff" disabled={staffQuery.isLoading}>
                  <SelectValue placeholder={staffQuery.isLoading ? "Loading staff…" : "Not assigned"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_RESPONSIBLE_STAFF}>Not assigned</SelectItem>
                  {staffOptions.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {staffQuery.isError ? (
                <p className="text-sm text-destructive">
                  The staff list could not be loaded. You can still save the other fields.
                </p>
              ) : null}
            </div>
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving…" : project ? "Save changes" : "Create project"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
